import { Common } from '@stockbit/ui';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { AdvancedSearchWrapper, InputDate, SearchInput } from './styled';
import { StreamState, selectors } from '../slice';
import {
  DATA_CY_SEARCH_STREAM_MENU_SEARCH,
  DATA_CY_SEARCH_STREAM_FILTER_FROM,
  DATA_CY_SEARCH_STREAM_FILTER_TO,
  DATA_CY_SEARCH_STREAM_SUBMIT,
} from './NavigationTab/__tests__/constant';

const { IconWrapper, Iconmoon, Flex, Text, Button } = Common;

interface SearchAdvancedProps {
  isActive?: boolean;
  onSearch: (data: Partial<StreamState['filterParams']>) => void;
  tabHeaderLabel: string; // 'Stream' | 'News' | 'Research'
}

function SearchAdvanced({
  isActive,
  onSearch,
  tabHeaderLabel,
}: SearchAdvancedProps) {
  const {
    filterParams: { keyword: defaultKeyword },
  } = useSelector(selectors);

  const inputRef = useRef(null);

  const [isAdvancedSearchVisible, setAdvancedSearchVisible] =
    useState<boolean>(false);
  const [keyword, setKeyword] = useState('');
  const [from, setFrom] = useState<string>(null);
  const [to, setTo] = useState<string>(null);

  const handleSubmit = (e: React.FormEvent<HTMLElement>) => {
    const data = {
      keyword,
      from: '',
      to: '',
    };

    if (isAdvancedSearchVisible) {
      if (from) {
        data.from = moment(from, 'YYYY-MM-DD').format('YYYY-MM-DD');
      }

      if (to) {
        data.to = moment(to, 'YYYY-MM-DD').format('YYYY-MM-DD');
      }
    }

    onSearch(data);
    e.preventDefault();
  };

  const onChangeKeyword = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;

    if (!value) {
      onSearch({
        keyword: '',
        from: '',
        to: '',
      });
    }

    setKeyword(value);
  };

  const onClickAdvancedSearchIcon = () =>
    setAdvancedSearchVisible(!isAdvancedSearchVisible);

  useEffect(() => {
    if (!isActive || !inputRef || !inputRef.current) {
      return;
    }

    inputRef.current.focus({
      cursor: 'start',
    });
  }, [isActive]);

  useEffect(() => {
    setKeyword(defaultKeyword);
  }, [defaultKeyword]);

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <SearchInput
          placeholder={`Search ${tabHeaderLabel}...`}
          width="100%"
          value={keyword}
          onChange={onChangeKeyword}
          onPressEnter={handleSubmit}
          allowClear
          suffix={
            <IconWrapper size="16px" onClick={onClickAdvancedSearchIcon}>
              <Iconmoon className="icon-toolbar_filter" size="16px" />
            </IconWrapper>
          }
          data-cy={DATA_CY_SEARCH_STREAM_MENU_SEARCH}
          ref={inputRef}
        />
        <AdvancedSearchWrapper active={isAdvancedSearchVisible}>
          <Text family="bold" height="30px">
            Advanced Search
          </Text>
          <Flex align="center">
            <Text>Search</Text>
            <InputDate
              placeholder="From"
              value={from}
              onChange={setFrom}
              suffixIcon={<div />}
              format="YYYY-MM-DD"
              data-cy={DATA_CY_SEARCH_STREAM_FILTER_FROM}
            />
            <InputDate
              placeholder="To"
              value={to}
              onChange={setTo}
              suffixIcon={<div />}
              format="YYYY-MM-DD"
              data-cy={DATA_CY_SEARCH_STREAM_FILTER_TO}
            />
            <Button
              type="primary"
              htmlType="submit"
              height="30px"
              ml="10px"
              data-cy={DATA_CY_SEARCH_STREAM_SUBMIT}
            >
              <Text color="inherit">Search</Text>
            </Button>
          </Flex>
        </AdvancedSearchWrapper>
        <input type="submit" style={{ display: 'none' }} />
      </form>
    </div>
  );
}

export default SearchAdvanced;
